import {useUser} from '@dofleini/security';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {navigation as defaultNavigationConfig} from '@/settings';

/**
 * Process a navigation
 *   1. translate the title
 *   1. Filter and process each navigation child inside the menu field
 * @param {function} t translation function
 * @param {function} hasPermission function to check permission
 * @param {Object} navigation object
 * @param {String} path parent path
 * @param {String} user current user object
 * @param {Boolean} isParentMenu if the element is a parent menu
 * @return {Object} navigation parsed
 * */
function processNavigation(t, hasPermission, {title, menu, link, ...rest}, path, user, isParentMenu) {
  const currentPath = (path || '') + (rest.path || '');
  const currentLink = link ? currentPath + link : link;
  menu = menu ? processMenu(t, hasPermission, menu, currentPath, user, isParentMenu) : [];
  
  title = t(title || currentLink);
  
  return {
    title,
    menu: menu.length ? menu : undefined, //not have children
    link: currentLink,
    ...rest,
    isParentMenu: !!menu.length
  };
}

/**
 * Process a menu list
 *   1. filter by permissions
 *   1. process each child navigation
 * @param {function} t translation function
 * @param {function} hasPermission function to check permission
 * @param {Array} menus array of navigations
 * @param {String} path parent path
 * @param {String} user current user object
 * @param {Boolean} isParentMenu if the element is a parent menu
 * @return {Object} menu parsed
 * */
function processMenu(t, hasPermission, menus = [], path, user, isParentMenu) {
  const filterMapped = menus.map(navigation => processNavigation(t, hasPermission, navigation, path, user, isParentMenu));
  
  return filterMapped
    .filter((menu) => {
      if (!menu) return false;
      
      const allow = menu.permissions && hasPermission(menu.permissions, menu?.isParentMenu);
      const allowByType = menu.userTypes ? ((!menu.noAdminLink && hasPermission('ADMIN')) || menu.userTypes.includes(user?.userType)) : true;
      return !menu.permissions || (allow && allowByType);
    })
    .map((menu) => {
      delete menu.isParentMenu;
      return menu;
    });
}

/**
 * Process a navigation
 * @param {function} t :translation function
 * @param {function} hasPermission function to check permission
 * @param {Object | Array} navigation navigation object
 * @param {Object | Array} user current user object
 * @return {Object | Array} navigations parsed
 * */
function processAllNavigation(t, hasPermission, navigation, user) {
  if (Array.isArray(navigation))
    return processMenu(t, hasPermission, navigation, null, user, true);
  return {
    menu: processMenu(t, hasPermission, navigation.menu, null, user)
  };
}

const useNavigation = (navigation = defaultNavigationConfig) => {
  const {hasPermission, user} = useUser();
  const {t} = useTranslation('navigation');
  
  return useMemo(() => {
    return processAllNavigation(t, hasPermission, navigation, user);
  }, [hasPermission, navigation, t, user]);
};

export default useNavigation;
